import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUserBalance } from '../entities/user/hooks/useUserBalance';
import { useGameSession } from '../features/game/hooks/useGameSession';
import { useGameTimer } from '../features/game/hooks/useGameTimer';
import { useRankings } from '../features/ranking/hooks/useRankings';
import { useStockData } from '../features/stock-data/hooks/useStockData';
import { tradingHistoryService } from '../features/trading-history/services/tradingHistoryService';
import { useTrading } from '../features/trading/hooks/useTrading';
import { AuthUser } from '../services/authService';
import { invalidateMyPageCache } from '../services/mypageService';
import { invalidateUserRankCache } from '../services/userRankService';
import {
    ChartData,
    GameEndData,
    GameState,
    INITIAL_BALANCE,
    Trade,
} from '../shared/types/game.types';
import { calculateMA } from '../shared/utils/calculations';
import { formatBalance, formatPrice, formatTime } from '../shared/utils/format';
import { adjustToTickSize, getTickSize } from '../shared/utils/tickSize';
import { calculateStockValue } from '../shared/utils/tradingFees';

// 타입 export (다른 컴포넌트에서 사용)
export type { RankingEntry, Trade } from '../shared/types/game.types';

interface UseGameStateProps {
  apiBaseUrl: string;
  user: AuthUser | null;
  accessToken: string | null;
  refreshAccessToken: () => Promise<string | null>;
  updateUser: (user: AuthUser) => void;
}

/**
 * 게임 상태 관리 메인 훅 (오케스트레이터)
 */
export const useGameState = ({
  apiBaseUrl,
  user,
  accessToken,
  refreshAccessToken,
  updateUser,
}: UseGameStateProps) => {
  // UI 상태
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [quantity, setQuantity] = useState('');
  const [tradeHistory, setTradeHistory] = useState<Trade[]>([]);
  const [isMarketOrder, setIsMarketOrder] = useState(true);
  const [limitOrderPrice, setLimitOrderPrice] = useState('');
  const [activeTradeTab, setActiveTradeTab] = useState<'buy' | 'sell'>('buy');
  const [showOHLCInfo, setShowOHLCInfo] = useState(true);
  const [showBalanceResetPopup, setShowBalanceResetPopup] = useState(false);
  const [toast, setToast] = useState<{
    id: number;
    message: string;
    type: 'positive' | 'negative' | 'neutral' | 'tip';
  } | null>(null);

  const autoModeIntervalRef = useRef<number | null>(null);
  const toastTimeoutRef = useRef<number | null>(null);

  const dismissToast = useCallback(() => {
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast(null);
  }, []);

  const notify = useCallback(
    (
      message: string,
      type: 'positive' | 'negative' | 'neutral' | 'tip' = 'neutral',
      durationMs = 2200
    ) => {
      if (!message) return;
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
        toastTimeoutRef.current = null;
      }

      const id = Date.now();
      setToast({ id, message, type });

      toastTimeoutRef.current = window.setTimeout(() => {
        setToast(current => (current?.id === id ? null : current));
        toastTimeoutRef.current = null;
      }, durationMs);
    },
    []
  );

  // 최신 상태를 추적하기 위한 ref
  const gameStateRef = useRef<GameState | null>(null);
  const isAutoModeRef = useRef<boolean>(true);
  const generateNextCandleInternalRef = useRef<
    | ((
        getNextCachedData: () => ChartData | null,
        fetchAdditionalData: () => Promise<ChartData[] | null>,
        isAutoMode: boolean
      ) => ChartData | null)
    | null
  >(null);
  const getNextCachedDataRef = useRef<(() => ChartData | null) | null>(null);
  const fetchAdditionalDataRef = useRef<
    (() => Promise<ChartData[] | null>) | null
  >(null);
  const processLimitOrdersRef = useRef<((candle: ChartData) => void) | null>(
    null
  );

  // Feature hooks
  const rankings = useRankings({ apiBaseUrl });
  const stockData = useStockData({
    accessToken,
    apiBaseUrl,
    refreshAccessToken,
  });
  const { persistBalance } = useUserBalance({
    user,
    accessToken,
    apiBaseUrl,
    refreshAccessToken,
    updateUser,
  });

  // Game session
  const gameSession = useGameSession({
    user,
    accessToken,
    apiBaseUrl,
    refreshAccessToken,
    updateUser,
    onGameStart: useCallback(
      (backgroundData: ChartData[], firstCandle: ChartData) => {
        // 게임 시작 시 처리할 로직
        console.log('게임 시작:', { backgroundData, firstCandle });
      },
      []
    ),
    onGameEnd: useCallback(
      async (gameEndData: GameEndData) => {
        // 게임 종료 시 처리할 로직
        console.log('게임 종료:', gameEndData);

        // 총자산 계산 (보유현금 + 주식 평가액)
        // 주식 평가액은 매도 수수료와 거래세를 차감한 금액
        const stockValue = calculateStockValue(
          gameEndData.finalCurrentPrice,
          gameEndData.finalStockQuantity
        );
        const totalAssets = gameEndData.finalBalance + stockValue;
        const normalizedTotalAssets = Math.max(Math.floor(totalAssets), 0);

        // 헤더의 잔고 표시 업데이트 (user.balance 업데이트)
        if (user) {
          updateUser({
            ...user,
            balance: normalizedTotalAssets,
          });
        }

        // 랭킹 캐시 무효화 (잔고 변경으로 랭킹이 변경될 수 있음)
        invalidateUserRankCache();
        
        // 마이페이지 캐시 무효화 (잔고, 게임 횟수, 업적/칭호 등이 변경될 수 있음)
        invalidateMyPageCache();

        // 거래 내역 저장 (백그라운드에서 처리, 에러가 발생해도 게임 종료는 계속 진행)
        console.log('📊 거래 내역 저장 조건 확인:', {
          hasUser: !!user,
          hasStockInfo: !!stockData.stockInfo,
          tradeHistoryLength: gameEndData.finalTradeHistory.length,
          stockInfo: stockData.stockInfo,
          userEmail: user?.email,
        });

        // 거래 내역이 없어도 게임 세션은 저장 (거래 없이 게임 종료한 경우도 기록)
        if (
          user &&
          stockData.stockInfo
        ) {
          // 게임 날짜 정보는 게임 시작 시점에 이미 설정됨
          // gameStartDate: 게임 시작 시점의 첫 번째 봉 날짜
          // gameEndDate: 게임 시작 시점부터 50봉 뒤의 날짜 (게임이 진행될 수 있는 최대 기간의 마지막 날짜)
          // 실제 매매 내역은 이 기간 동안의 거래 내역만 포함됨

          console.log('📅 게임 날짜 정보:', {
            gameStartDate: stockData.stockInfo.gameStartDate,
            gameEndDate: stockData.stockInfo.gameEndDate,
            stockInfo: stockData.stockInfo,
          });

          // stockInfo는 이미 gameStartDate와 gameEndDate가 설정되어 있음
          const stockInfoWithDates = {
            ...stockData.stockInfo,
          };

          console.log('💾 거래 내역 저장 시작...');
          (async () => {
            try {
              const unlockedAchievements =
                await tradingHistoryService.saveGameTradingHistory(
                  apiBaseUrl,
                  stockInfoWithDates,
                  gameEndData.startingBalance,
                  normalizedTotalAssets,
                  gameEndData.finalTradeHistory
                );
              console.log('✅ 거래 내역 저장 성공');

              // 획득한 칭호/업적이 있으면 gameEndData에 추가
              if (unlockedAchievements) {
                gameSession.setGameEndData((prev) => {
                  if (prev) {
                    return {
                      ...prev,
                      unlockedAchievements: {
                        unlockedTitles:
                          unlockedAchievements.unlockedTitles || [],
                        unlockedAchievements:
                          unlockedAchievements.unlockedAchievements || [],
                      },
                    };
                  }
                  return prev;
                });

                // 획득 알림 표시
                if (
                  unlockedAchievements.unlockedTitles.length > 0 ||
                  unlockedAchievements.unlockedAchievements?.length > 0
                ) {
                  console.log('🎉 칭호/업적 획득:', unlockedAchievements);
                }
              }
            } catch (error) {
              console.error('❌ 거래 내역 저장 실패:', error);
              console.error('❌ 에러 상세:', {
                message: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
              });
              // 에러가 발생해도 게임 종료는 계속 진행
            }
          })();
        } else {
          console.warn('⚠️ 거래 내역 저장 조건 미충족:', {
            user: !!user,
            stockInfo: !!stockData.stockInfo,
            tradeHistoryLength: gameEndData.finalTradeHistory.length,
          });
        }

        // 거래 내역 초기화
        setTradeHistory([]);
        setQuantity('');
        setLimitOrderPrice('');
      },
      [setTradeHistory, user, updateUser, stockData.stockInfo, apiBaseUrl]
    ),
    getTradeHistory: useCallback(() => tradeHistory, [tradeHistory]),
  });

  // Trading
  const trading = useTrading({
    gameState: gameSession.gameState,
    setGameState: gameSession.setGameState,
    quantity,
    setQuantity,
    limitOrderPrice,
    setLimitOrderPrice,
    isMarketOrder,
    tradeHistory,
    setTradeHistory,
    notify,
  });

  // Game timer
  const handleTimeUp = useCallback(() => {
    gameSession.endGame();
    gameSession.setShowGameEndPopup(true);
  }, [gameSession]);

  useGameTimer({
    isGameActive: gameSession.gameState.isGameActive,
    timeLeft: gameSession.timeLeft,
    setTimeLeft: gameSession.setTimeLeft,
    onTimeUp: handleTimeUp,
  });

  // ref 업데이트
  useEffect(() => {
    gameStateRef.current = gameSession.gameState;
    isAutoModeRef.current = gameSession.isAutoMode;
    generateNextCandleInternalRef.current =
      gameSession.generateNextCandleInternal;
    getNextCachedDataRef.current = stockData.getNextCachedData;
    fetchAdditionalDataRef.current = stockData.fetchAdditionalData;
    processLimitOrdersRef.current = trading.processLimitOrders;
  }, [
    gameSession.gameState,
    gameSession.isAutoMode,
    gameSession.generateNextCandleInternal,
    stockData.getNextCachedData,
    stockData.fetchAdditionalData,
    trading.processLimitOrders,
  ]);

  // Auto mode candle generation
  useEffect(() => {
    // 기존 interval 정리
    if (autoModeIntervalRef.current) {
      clearInterval(autoModeIntervalRef.current);
      autoModeIntervalRef.current = null;
    }

    const currentGameState = gameStateRef.current;
    const currentIsAutoMode = isAutoModeRef.current;

    if (
      !currentGameState ||
      !currentGameState.isGameActive ||
      !currentIsAutoMode
    ) {
      console.log('자동 모드 캔들 생성 스킵:', {
        hasGameState: !!currentGameState,
        isGameActive: currentGameState?.isGameActive,
        isAutoMode: currentIsAutoMode,
      });
      return;
    }

    console.log('자동 모드 캔들 생성 시작:', {
      isGameActive: currentGameState.isGameActive,
      isAutoMode: currentIsAutoMode,
      candleCount: currentGameState.candleCount,
    });

    autoModeIntervalRef.current = window.setInterval(() => {
      // ref를 통해 최신 상태 가져오기
      const latestGameState = gameStateRef.current;
      const latestIsAutoMode = isAutoModeRef.current;
      const latestGenerateNextCandle = generateNextCandleInternalRef.current;
      const latestGetNextCachedData = getNextCachedDataRef.current;
      const latestFetchAdditionalData = fetchAdditionalDataRef.current;
      const latestProcessLimitOrders = processLimitOrdersRef.current;

      if (
        !latestGameState ||
        !latestGenerateNextCandle ||
        !latestGetNextCachedData ||
        !latestFetchAdditionalData ||
        !latestProcessLimitOrders
      ) {
        console.log('자동 모드 캔들 생성 중단: 필요한 참조가 없음');
        return;
      }

      console.log('자동 모드 캔들 생성 시도:', {
        isGameActive: latestGameState.isGameActive,
        isAutoMode: latestIsAutoMode,
        candleCount: latestGameState.candleCount,
      });

      // 현재 상태 확인
      if (!latestGameState.isGameActive || !latestIsAutoMode) {
        console.log('자동 모드 캔들 생성 중단: 게임 비활성 또는 수동 모드');
        if (autoModeIntervalRef.current) {
          clearInterval(autoModeIntervalRef.current);
          autoModeIntervalRef.current = null;
        }
        return;
      }

      // 최신 함수 참조를 사용하여 캔들 생성
      const newCandle = latestGenerateNextCandle(
        () => latestGetNextCachedData(),
        async () => {
          const additional = await latestFetchAdditionalData();
          return additional;
        },
        latestIsAutoMode
      );

      console.log('자동 모드 캔들 생성 결과:', {
        newCandle: !!newCandle,
        candleCount: latestGameState.candleCount + 1,
      });

      if (newCandle) {
        // Limit order 처리
        latestProcessLimitOrders(newCandle);
      }
    }, 3000);

    return () => {
      console.log('자동 모드 캔들 생성 중지');
      if (autoModeIntervalRef.current) {
        clearInterval(autoModeIntervalRef.current);
        autoModeIntervalRef.current = null;
      }
    };
  }, [gameSession.gameState.isGameActive, gameSession.isAutoMode]);

  // Limit order processing on candle update
  useEffect(() => {
    if (!gameSession.gameState.isGameActive) return;

    const latestCandle =
      gameSession.gameState.history[gameSession.gameState.history.length - 1];
    if (!latestCandle) return;

    trading.processLimitOrders(latestCandle);
  }, [
    gameSession.gameState.history,
    gameSession.gameState.isGameActive,
    trading,
  ]);

  // 창/탭 닫기 시 경고 + 종료 처리(베스트 에포트)
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!gameSession.gameState.isGameActive) return;
      // 최신 브라우저는 커스텀 문구를 표시하지 않지만, 경고 다이얼로그 자체는 띄울 수 있음
      e.preventDefault();
      e.returnValue = '';
      // 베스트 에포트로 종료 처리(전량 청산 trade 포함)
      gameSession.endGame();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [gameSession]);

  // 잔고 리셋
  const resetBalance = useCallback(async () => {
    if (!user) return;

    try {
      const INITIAL_BALANCE = 10000000;
      await persistBalance(INITIAL_BALANCE, 0);

      updateUser({
        ...user,
        balance: INITIAL_BALANCE,
      });

      setShowBalanceResetPopup(false);
      console.log('✅ 잔고가 1000만원으로 리셋되었습니다.');
    } catch (error) {
      console.error('❌ 잔고 리셋 실패:', error);
      alert('잔고 리셋에 실패했습니다. 다시 시도해주세요.');
    }
  }, [user, persistBalance, updateUser]);

  // 게임 시작
  const startGame = useCallback(async () => {
    // 잔고 체크: 잔고가 0원이면 리셋 팝업 표시
    if (user && user.balance <= 0) {
      setShowBalanceResetPopup(true);
      return;
    }

    try {
      console.log('🎮 게임 시작 중...');
      const { serverData, stockCode, stockName } =
        await stockData.fetchStockData();

      if (
        !serverData ||
        !Array.isArray(serverData) ||
        serverData.length === 0
      ) {
        throw new Error('서버에서 받은 데이터가 없거나 유효하지 않습니다.');
      }

      const { backgroundData, gameData } = stockData.convertToChartData(
        serverData,
        stockCode,
        stockName
      );

      gameSession.startGameInternal(backgroundData, gameData);
    } catch (error) {
      console.error('❌ 서버에서 데이터를 가져오는데 실패했습니다:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : '서버에서 데이터를 가져오는데 실패했습니다.';

      alert(
        `게임 시작 실패\n\n오류: ${errorMessage}\n\n콘솔을 확인하여 자세한 내용을 확인해주세요.`
      );
    }
  }, [stockData, gameSession, user]);

  // 다음 캔들 생성 (수동 모드)
  const generateNextCandle = useCallback(() => {
    const newCandle = gameSession.generateNextCandleInternal(
      stockData.getNextCachedData,
      async () => {
        const additional = await stockData.fetchAdditionalData();
        return additional;
      },
      gameSession.isAutoMode
    );

    if (newCandle) {
      trading.processLimitOrders(newCandle);
    }
  }, [gameSession, stockData, trading]);

  // 게임 상태 리셋
  const resetGameState = useCallback(
    (balance: number) => {
      gameSession.resetGameState(balance);
      setTradeHistory([]);
      setQuantity('');
      setLimitOrderPrice('');
      stockData.resetStockData();
    },
    [gameSession, stockData]
  );

  // 사용자 잔고 동기화 (최초 로그인 시에만)
  useEffect(() => {
    if (user) {
      gameSession.setGameState(prev => {
        // 이미 게임이 시작된 경우 잔고를 덮어쓰지 않음
        if (prev.isGameActive) {
          return prev;
        }
        // 게임이 시작되지 않은 경우에만 동기화
        return {
          ...prev,
          playerBalance: user.balance,
          initialBalance: user.balance,
        };
      });
      return;
    }

    resetGameState(INITIAL_BALANCE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]); // user 객체 전체가 아닌 email만 의존성으로 사용

  // 랭킹 로드 (싱글톤이므로 안전하게 호출 가능)
  useEffect(() => {
    rankings.loadRankings();
  }, [rankings.loadRankings]); // 이제 안정된 함수 참조이므로 안전

  // 이동평균 계산
  const ma5 = useMemo(() => {
    return calculateMA(gameSession.gameState.history, 5);
  }, [gameSession.gameState.history]);

  const ma10 = useMemo(() => {
    return calculateMA(gameSession.gameState.history, 10);
  }, [gameSession.gameState.history]);

  const ma20 = useMemo(() => {
    return calculateMA(gameSession.gameState.history, 20);
  }, [gameSession.gameState.history]);

  const ma60 = useMemo(() => {
    return calculateMA(gameSession.gameState.history, 60);
  }, [gameSession.gameState.history]);

  const ma120 = useMemo(() => {
    return calculateMA(gameSession.gameState.history, 120);
  }, [gameSession.gameState.history]);

  // 변화율 계산
  const changeRate = useMemo(() => {
    if (gameSession.gameState.history.length < 2) return 0;
    const first = gameSession.gameState.history[0].open;
    const last = gameSession.gameState.currentPrice;
    return ((last - first) / first) * 100;
  }, [gameSession.gameState.currentPrice, gameSession.gameState.history]);

  // 토큰 갱신은 AuthContext에서 처리하므로 여기서는 제거
  // 중복 호출 방지를 위해 useGameState에서는 토큰 갱신 스케줄링을 하지 않음

  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return {
    state: {
      gameState: gameSession.gameState,
      timeLeft: gameSession.timeLeft,
      theme,
      quantity,
      tradeHistory,
      isAutoMode: gameSession.isAutoMode,
      showGameEndPopup: gameSession.showGameEndPopup,
      showExitConfirmPopup: gameSession.showExitConfirmPopup,
      gameEndData: gameSession.gameEndData,
      isMarketOrder,
      limitOrderPrice,
      activeTradeTab,
      showOHLCInfo,
      showBalanceResetPopup,
      toast,
      stockInfo: stockData.stockInfo,
      rankings: rankings.rankings,
      isRankingLoading: rankings.isRankingLoading,
      rankingError: rankings.rankingError,
      rankingCurrentPage: rankings.currentPage,
      rankingTotalPages: rankings.totalPages,
      rankingsAsOf: (rankings as any).rankingsAsOf as string | null,
      changeRate,
      ma5,
      ma10,
      ma20,
      ma60,
      ma120,
    },
    setters: {
      setQuantity,
      setIsAutoMode: gameSession.setIsAutoMode,
      setShowGameEndPopup: gameSession.setShowGameEndPopup,
      setShowExitConfirmPopup: gameSession.setShowExitConfirmPopup,
      setIsMarketOrder,
      setLimitOrderPrice,
      setActiveTradeTab,
      setShowOHLCInfo,
      setShowBalanceResetPopup,
    },
    actions: {
      toggleTheme,
      loadRankings: rankings.loadRankings,
      changeRankingPage: rankings.changePage,
      startGame,
      generateNextCandle,
      buy: trading.buy,
      sell: trading.sell,
      cancelLimitOrder: trading.cancelLimitOrder,
      calculateBuyPercentage: trading.calculateBuyPercentage,
      calculateSellPercentage: trading.calculateSellPercentage,
      endGame: gameSession.endGame,
      resetGameState,
      setTimeLeft: gameSession.setTimeLeft,
      setTradeHistory,
      resetBalance,
      notify,
      dismissToast,
    },
    helpers: {
      getTickSize,
      adjustToTickSize,
      formatPrice,
      formatBalance,
      formatTime,
    },
  };
};

export type UseGameStateReturn = ReturnType<typeof useGameState>;
